import { Component, forwardRef, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, FormArray, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FieldType } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { isNil } from 'lodash';
import { positiveOrZeroNumber } from '@shared/formly/validators';
import { round } from '@core/utilities/utilities.constants';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { WCAGLabelDirective } from '@shared/directives/wcag-label.directive';

@UntilDestroy()
@Component({
  selector: 'formly-key-value-pair',
  template: `
    <div [formGroup]="subFormGroup" class="mb-3">
    <p-table [ngClass]="'eccc'" [value]="dummy">
      <ng-template pTemplate="header">
        <tr>
          <th *ngFor="let header of props.keyValueConfig.headers"
              [innerHTML]="translationPrefix + header | translate"></th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body">
        <tr *ngFor="let prop of props.keyValueConfig.keys">
          <td>
            <span *ngIf="prop.required" class="wcag-visuallyhidden">Required field, </span>
            <span [innerHTML]="translationPrefix + prop.key | translate"></span>
            <span *ngIf="prop.required" aria-hidden="true">*</span>:
          </td>
          <td>
            <ng-container *ngIf="prop.type === 'number'; else NORMAL">
              <label class="wcag-visuallyhidden" [for]="prop.key">{{ prop.key }}</label>
              <input
                [allowNegativeNumbers]="prop.allowNegativeNumbers"
                [class.is-invalid]="isInvalid(prop.key) || isLessThanZero(prop.key)"
                [formlyAttributes]="field"
                [formControlName]="prop.key"
                maxlength="19"
                [id]="prop.key"
                [mask]="prop.mask || 'separator.0'"
                [required]="prop.required"
                [thousandSeparator]="commaSeparator"
                [decimalMarker]="'.'"
                [ngModel]="_model[prop.key]"
                (ngModelChange)="updateModel($event, prop)"
                class="form-control"
                type="text"
              />
              <div class="invalid-feedback" *ngIf="isInvalid(prop.key); else CHECK_NEGATIVE_VALUE">
                <span [innerHTML]="'COMMON.messageSection.fieldRequiredException' | translate"></span>
              </div>
              <ng-template #CHECK_NEGATIVE_VALUE>
                <div class="invalid-feedback" *ngIf="isLessThanZero(prop.key)">
                  <span [innerHTML]="'COMMON.messageSection.valueCannotBeLessThanZero' | translate"></span>
                </div>
              </ng-template>
            </ng-container>
            <ng-template #NORMAL>
              <label class="wcag-visuallyhidden" [for]="prop.key">{{ prop.key }}</label>
              <input
                class="form-control"
                [formControl]="form[controlId][prop.key]"
                [class.is-invalid]="isInvalid(prop.key)"
                [required]="prop.required"
                [formControlName]="prop.key"
                (ngModelChange)="updateModel($event, prop)"
              />
              <div class="invalid-feedback" *ngIf="isInvalid(prop.key)">
                <span [innerHTML]="'COMMON.messageSection.fieldRequiredException' | translate"></span>
              </div>
            </ng-template>
          </td>
        </tr>
      </ng-template>
      <ng-container *ngIf="props.keyValueConfig.summary">
      <ng-template pTemplate="footer">
        <tr>
          <td><span [innerHTML]="translationPrefix + props.keyValueConfig.summary.label | translate"></span></td>
          <td>
            <label class="wcag-visuallyhidden" [for]="'summary_' + props.keyValueConfig.summary.key">{{ 'summary_' + props.keyValueConfig.summary.key }}</label>
            <input
                wcag-label
                [formlyAttributes]="field"
                [disabled]="true"
                [id]="'summary_' + props.keyValueConfig.summary.key"
                [ngModelOptions]="{standalone: true}"
                [mask]="props.keyValueConfig.summary.mask || 'separator.0'"
                [thousandSeparator]="commaSeparator"
                [ngModel]="model[props.keyValueConfig.summary.key]"
                class="form-control"
                type="text"
              />
          </td>
        </tr>
      </ng-template>
      </ng-container>
    </p-table>
    </div>
  `,
  imports: [ReactiveFormsModule, CommonModule, TableModule, TranslateModule, FormsModule, WCAGLabelDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormlyKeyValuePairsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FormlyKeyValuePairsComponent),
      multi: true,
    },
  ],
})
// tslint:disable-next-line:component-class-suffix
export class FormlyKeyValuePairsComponent extends FieldType implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  dummy = [{}];
  _model = {};
  ready = false;
  commaSeparator;
  private _destroy$ = new Subject();

  public get controlId(): string {
    return this.props.controlId;
  }

  isInvalid(key: string) {
    return this.subFormGroup.controls[key].errors &&
      this.subFormGroup.controls[key].dirty &&
      this.subFormGroup.controls[key].errors.required;
  }

  isLessThanZero(key: string) {
    return this.subFormGroup.controls[key].errors &&
      this.subFormGroup.controls[key].dirty &&
      this.subFormGroup.controls[key].errors.positiveOrZeroNumber;
  }


  constructor(
    private translateSvc: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.commaSeparator = this.translateSvc.currentLang === 'en' ? ',' : ' ';
    if (this.form instanceof FormGroup) {
      this.form.addControl(this.controlId, this.subFormGroup);
    } else if (this.form instanceof FormArray) {
      this.form.push(this.subFormGroup);
    }
    // this.form.addControl(this.controlId, this.subFormGroup);
    if (!this.model[this.controlId]) {
      this.model[this.controlId] = {};
    } else {
      this._model = {...this.model[this.controlId]};
      Object.keys(this._model).forEach(key => this._model[key] = this._model[key].toFixed());
      this._model = {...this.model[this.controlId]};
      this.calculateSummary();
    }
    setTimeout(() => this.subFormGroup.markAsPristine());

    this.subFormGroup.statusChanges.subscribe(() => {
      this.onChange(this.subFormGroup.value);
      this.onTouched();
    });

    this.handleLanguageChange();
  }

  private handleLanguageChange() {
    this.translateSvc.onLangChange.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.commaSeparator = this.translateSvc.currentLang === 'en' ? ',' : ' ';
      this.props.keyValueConfig.keys.forEach(prop => {
        if (prop.mask) {
          const currentValue = this.subFormGroup.get(prop.key).value;
          if (currentValue !== null && currentValue !== undefined) {
            const cleanValue = String(currentValue).replace(/,|\s/g, '');
            setTimeout(() => {
              this.subFormGroup.get(prop.key).setValue(+cleanValue);
            });
          }

        }
      });
    });
  }

  writeValue(value: any): void {
    if (value) {
      this.form.setValue(value);
    } else {
      this.form.reset();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  get subFormGroup(): FormGroup {
    if (this.form.get(this.controlId)) {
      return this.form.get(this.controlId) as FormGroup;
    } else {
      const subForm: FormGroup = new FormGroup({});
      if (this.props.keyValueConfig && this.props.keyValueConfig.keys.length) {
        this.props.keyValueConfig.keys.forEach(prop => {
          const control = new FormControl('');
          if (prop.required) {
            control.setValidators([Validators.required, positiveOrZeroNumber]);
          }
          subForm.addControl(prop.key, control);
        });
      }
      return subForm as FormGroup;
    }
  }

  updateModel(val, prop) {
    this._model[prop.key] = prop.type === 'number' && val ? Number(val) : val;
    Object.keys(this._model).forEach(key => {
      if (isNil(this._model[key])) {
        delete this._model[key];
      }
    });
    this.field.model[this.controlId] = this._model;
    if (this.props.keyValueConfig.summary) {
      this.calculateSummary();
    }
    if (this.props.change) {
      this.props.change(this.field, {
        keys: this._model,
        summary: this.props.keyValueConfig.summary ? this.field.model[this.props.keyValueConfig.summary.key] : null,
      });
    }
  }

  private calculateSummary() {
    this.field.model[this.props.keyValueConfig.summary.key] = round(Object.keys(this._model).reduce((sum, key) => sum + Number(this._model[key]), 0), 5);
  }

  // get commaSeparator() {
  //   return this.translateSvc.currentLang === 'en' ? ',' : ' ';
  // }

  get translationPrefix(): string {
    return `${ this.props.translatePrefix }.` || '';
  }

  validate() {
    return this.subFormGroup.valid ? null : { invalid: true };
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.subFormGroup.statusChanges.subscribe(fn);
  }

  private onChange = (value: any) => {};
  private onTouched = () => {};

   ngOnDestroy(): void {
    this._destroy$.next(null);
    this._destroy$.complete();
  }

}
